// src/pages/EditBlog.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../QuillEditor.css';

const normalizeCard = (c = {}) => ({
  id: c.id ?? null,
  name: c.name || '',
  affiliate: c.affiliate || '',
  rating: typeof c.rating === 'number' ? c.rating : Number(c.rating || 0),
  cardImage: c['?column?'] || '',
  bankLogo: c.bank_url || '',
  bankName: c.bank_name || ''
});

const safeJsonParse = (str) => {
  try {
    if (!str || typeof str !== 'string') return {};
    return JSON.parse(str);
  } catch {
    try {
      const cleanedStr = str.replace(/'/g, '"').replace(/=>/g, ':');
      return JSON.parse(cleanedStr);
    } catch {
      return {};
    }
  }
};

/** ---------- Refreshed, cleaner styling ---------- */
const styles = {
  page: { display: 'flex', justifyContent: 'center', width: '100%', minHeight: '100vh', backgroundColor: '#fff' },
  container: { width: '84%', maxWidth: '1200px', padding: '2rem 0 4rem', fontFamily: 'Inter, system-ui, Arial, sans-serif' },
  actionsBar: { position: 'sticky', top: 0, zIndex: 5, display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '0.9rem 0', background: '#ffffff', boxShadow: '0 1px 0 rgba(15,23,42,0.08), 0 2px 8px rgba(15,23,42,0.06)' },
  divider: { height: '3px', width: '100%', borderRadius: '999px', background: 'linear-gradient(90deg,#2563EB,#7C3AED,#EC4899,#10B981)', opacity: 0.9, marginBottom: '1.2rem' },
  buttonPrimary: { padding: '12px 18px', backgroundColor: '#2563EB', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: 700 },
  buttonDark: { padding: '12px 18px', backgroundColor: '#111827', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: 700 },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0.75rem 0 1rem' },
  heading: { margin: 0, color: '#111827', fontSize: '1.5rem', fontWeight: 800, letterSpacing: '0.2px' },
  form: { display: 'grid', gridTemplateColumns: '1fr', gap: '1.6rem' },
  section: { padding: '1.35rem', borderRadius: '14px', backgroundColor: '#fff', border: '1px solid #e5e7eb', boxShadow: '0 6px 16px rgba(0,0,0,0.05)' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.25rem' },
  rowTall: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '0.5rem' },
  fullWidth: { marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  control: { display: 'flex', flexDirection: 'column', gap: '0.55rem' },
  label: { fontSize: '0.95rem', color: '#111827', fontWeight: 700, letterSpacing: '0.2px', marginTop: '0.15rem' },
  input: { width: '100%', padding: '12px', border: '1px solid #cfd8e3', borderRadius: '10px', fontSize: '0.95rem', background: '#fff', outline: 'none' },
  select: { width: '100%', padding: '12px', border: '1px solid #cfd8e3', borderRadius: '10px', background: '#fff', fontSize: '0.95rem', outline: 'none' },
  textarea: { width: '100%', minHeight: '140px', padding: '12px', border: '1px solid #cfd8e3', borderRadius: '10px', resize: 'vertical', fontSize: '0.95rem' },
  textareaWide: { width: '100%', minHeight: '160px', padding: '12px', border: '1px solid #cfd8e3', borderRadius: '10px', resize: 'vertical', fontSize: '0.95rem', marginBottom: '1.25rem' },
  previewImage: { maxWidth: '100%', maxHeight: '220px', objectFit: 'contain', marginTop: '12px', border: '1px solid #e5e7eb', borderRadius: '10px' },
  quillContainer: { border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'visible', position: 'relative', zIndex: 0 },
  suggestWrap: { position: 'relative' },
  suggestList: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 10px 18px rgba(0,0,0,0.08)', marginTop: '6px', maxHeight: '220px', overflowY: 'auto' },
  suggestItem: { padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' },
  suggestItemHover: { background: '#f3f4f6' },
  thumb: { width: 40, height: 24, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' },
  logo: { width: 24, height: 24, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' },
  tbWrap: { marginTop: '1rem', padding: '1rem', border: '1px dashed #d1d5db', borderRadius: 12, background: '#fafafa' },
  tbRow: { gap: '0.75rem', marginTop: '0.5rem', display: 'grid' },
  smallBtn: { padding: '9px 14px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 600 },
  dangerBtn: { padding: '9px 14px', borderRadius: 10, border: '1px solid #fecaca', background: '#fee2e2', color: '#991b1b', cursor: 'pointer', fontWeight: 700 },
  muted: { fontSize: 12, color: '#6b7280' },
  previewBox: { marginTop: '0.75rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.75rem', overflowX: 'auto' }
};

/** ---------- Escapers + block builders ---------- */
const escHTML = (s = '') => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escCSV = (s = '') => {
  const needsQuotes = /[",\n;]/.test(s) || s.includes(' ');
  const doubled = String(s).replace(/"/g, '""');
  return needsQuotes ? `"${doubled}"` : doubled;
};
const buildDelimited = (table) => {
  const nonEmptyRows = table.rows.filter(r => r.some(c => (c ?? '').trim() !== ''));
  if (!nonEmptyRows.length || table.cols.length === 0) return '';
  const d = table.delimiter || ',';
  const rs = table.rowSeparator || ';';
  const header = table.hasHeader ? table.cols.map(escCSV).join(d) : '';
  const rows = nonEmptyRows.map(r => r.map(escCSV).join(d));
  const parts = header ? [header, ...rows] : rows;
  return parts.join(` ${rs} `);
};
const buildMetaBlock = (table) => {
  const binding = (table.binding || 'table_1').trim();
  const title = (table.title || '').trim();
  const delimited = buildDelimited(table) || '';
  return `• {${binding}} — "${title}"\n\n  ${delimited}\n\n`;
};
const buildTableHTML = (t) => {
  const nonEmptyRows = t.rows.filter(r => r.some(c => (c ?? '').trim() !== ''));
  if (!nonEmptyRows.length || t.cols.length === 0) return '';
  const header = t.hasHeader ? `<thead><tr>${t.cols.map(h => `<th>${escHTML(h)}</th>`).join('')}</tr></thead>` : '';
  const body = `<tbody>${nonEmptyRows.map(r => `<tr>${r.map(c => `<td>${escHTML(c)}</td>`).join('')}</tr>`).join('')}</tbody>`;
  const titleBlock = t.title?.trim() ? `<caption style="caption-side:top;text-align:left;font-weight:600;margin:0 0 .5rem 0">${escHTML(t.title.trim())}</caption>` : '';
  return `
    <div class="ql-table-wrapper" data-table-id="${t.id}">
      <table class="ql-table">
        ${titleBlock}
        ${header}
        ${body}
      </table>
    </div>
  `;
};

/** ---------- Replace-or-insert helpers ---------- */
const replaceOrInsertInQuill = (quill, binding, block) => {
  const full = quill.getText();
  const re = new RegExp(`•\\s*\\{${binding}\\}[\\s\\S]*?(?:\\n\\s*\\n)`, 'm');
  const idx = full.search(re);
  if (idx !== -1) {
    const m = full.match(re);
    quill.deleteText(idx, m[0].length, 'user');
    quill.insertText(idx, block, 'user');
  } else {
    quill.insertText(quill.getLength(), block, 'user');
  }
};
const replaceOrInsertInString = (src, binding, block) => {
  const re = new RegExp(`•\\s*\\{${binding}\\}[\\s\\S]*?(?:\\n\\s*\\n)`, 'm');
  return re.test(src || '') ? (src || '').replace(re, block) : `${(src || '').trim() ? `${src}\n\n` : ''}${block}`;
};

export default function EditBlog({ blogs, setBlogs }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    heading: '',
    content: '',
    previewImage: '',
    previewText: '',
    youtubeLink: '',
    tags: '',
    status: 1,
    type: '',
    priority: 1,
    source_id: '1',
    affiliate: '',
    rating: 0,
    cardImage: '',
    bankLogo: '',
    card_id: 1
  });

  const [meta, setMeta] = useState({ details: '', overview: '', benefits: '', fees: '' });
  const [loading, setLoading] = useState(true);
  const [previewImageFile, setPreviewImageFile] = useState(null);
  const [focused, setFocused] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(-1);

  // Cards
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState('');

  /** ---------- TABLE STATE (edit-friendly) ---------- */
  const [tables, setTables] = useState([{
    id: crypto?.randomUUID ? crypto.randomUUID() : String(Date.now()),
    title: '', // no default title
    hasHeader: true,
    cols: ['Label', 'Value'],
    rows: [['', '']],
    delimiter: ',',
    rowSeparator: ';',
    binding: 'table_1'
  }]);
  const [activeTableId, setActiveTableId] = useState(null);
  useEffect(() => { if (!activeTableId && tables.length) setActiveTableId(tables[0].id); }, [tables, activeTableId]);
  const activeIdx = tables.findIndex(t => t.id === activeTableId);
  const activeTable = activeIdx >= 0 ? tables[activeIdx] : tables[0];

  /** ---------- Quill refs ---------- */
  const quillRefs = {
    content: useRef(null),
    previewText: useRef(null),
    metaDetails: useRef(null),
    benefits: useRef(null) // Benefits uses Quill
  };

  /** ---------- Fetch existing blog ---------- */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`https://admin.yaper.co/api/v1/content_pages/${slug}`);
        const json = await res.json();
        if (json?.data) {
          const b = json.data;
          const parsedMeta = b.meta ? safeJsonParse(b.meta) : {};
          setForm(p => ({
            ...p,
            heading: b.heading || '',
            content: b.content || '',
            previewImage: b.preview_img || '',
            previewText: b.preview_text || '',
            youtubeLink: b.youtube_link || '',
            tags: b.tags || '',
            status: b.status ?? 1,
            type: b.content_type || 'blog',
            priority: b.priority ?? 1,
            source_id: b.source_id || '1',
            affiliate: b.affiliate || '',
            rating: b.rating || 0,
            cardImage: b.card_image || '',
            bankLogo: b.bank_logo || '',
            card_id: b.card_id || 1
          }));
          setMeta({
            details: parsedMeta.details || '',
            overview: parsedMeta.overview || '',
            benefits: parsedMeta.benefits || '',
            fees: parsedMeta.fees || ''
          });

          // Seed tables
          if (Array.isArray(parsedMeta?.tables) && parsedMeta.tables.length) {
            const seeded = parsedMeta.tables.map((t, i) => ({
              id: t.id || (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + i)),
              title: t.title || '', // no default text
              hasHeader: !!t.hasHeader,
              cols: Array.isArray(t.cols) && t.cols.length ? t.cols : ['Label', 'Value'],
              rows: Array.isArray(t.rows) && t.rows.length ? t.rows : [['', '']],
              delimiter: t.delimiter || ',',
              rowSeparator: t.rowSeparator || ';',
              binding: t.binding || `table_${i + 1}`
            }));
            setTables(seeded);
            setActiveTableId(seeded[0].id);
          }
        }
      } catch (e) {
        console.error('Fetch blog failed:', e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  /** ---------- Cards fetch ---------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setCardsLoading(true);
        setCardsError('');
        const res = await fetch('https://admin.yaper.co/api/v1/cards/get_cards');
        if (!res.ok) throw new Error(`Cards API responded ${res.status}`);
        const json = await res.json();
        const list = Array.isArray(json?.cards) ? json.cards : [];
        const normalized = list.map(normalizeCard).filter(c => c.name);
        if (!cancelled) setCards(normalized);
      } catch (e) {
        if (!cancelled) setCardsError('Failed to load card suggestions.');
        console.error('Cards fetch failed:', e);
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /** ---------- Quill helpers ---------- */
  const uploadAsset = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('https://admin.yaper.co/api/v1/content_pages/upload_asset', { method: 'POST', body: fd });
    if (!res.ok) throw new Error(`Server responded with status: ${res.status}`);
    const data = await res.json();
    return data.url;
  };
  const imageHandler = (quillRef) => () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*, video/*'; input.click();
    input.onchange = async () => {
      const file = input.files?.[0]; if (!file) return;
      try {
        const url = await uploadAsset(file);
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, file.type.startsWith('image/') ? 'image' : 'video', url);
      } catch { alert('Asset upload failed. Please try again.'); }
    };
  };
  const getModules = (ref) => ({
    toolbar: {
      container: [[{ header: [1,2,3,4,5,6,false] }], ['bold','italic','underline','strike','blockquote'], [{ list:'ordered' }, { list:'bullet' }, { indent:'-1' }, { indent:'+1' }], ['link','image'], ['clean']],
      handlers: { image: imageHandler(ref) }
    }
  });
  const formats = ['header','bold','italic','underline','strike','blockquote','list','bullet','indent','link','image'];

  const contentModules = useMemo(() => getModules(quillRefs.content), [quillRefs.content]);
  const previewTextModules = useMemo(() => getModules(quillRefs.previewText), [quillRefs.previewText]);
  const metaDetailsModules = useMemo(() => getModules(quillRefs.metaDetails), [quillRefs.metaDetails]);
  const benefitsModules = useMemo(() => getModules(quillRefs.benefits), [quillRefs.benefits]);

  /** ---------- Simple setters ---------- */
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'previewImage') setPreviewImageFile(null);
    setForm(p => ({ ...p, [name]: value }));
  };
  const handleMetaChange = (e) => { const { name, value } = e.target; setMeta(m => ({ ...m, [name]: value })); };
  const previewImageHandler = (e) => {
    const file = e.target.files?.[0];
    if (file) { setPreviewImageFile(file); setForm(p => ({ ...p, previewImage: URL.createObjectURL(file) })); }
  };

  /** ---------- Suggestions ---------- */
  const filteredSuggestions = useMemo(() => {
    const q = (form.heading || '').trim().toLowerCase();
    if (!q) return [];
    return cards.filter(c => c.name.toLowerCase().includes(q)).slice(0, 6);
  }, [form.heading, cards]);
  const applySuggestion = (item) => {
    setForm(p => ({
      ...p,
      card_id: item.id || p.card_id,
      heading: item.name,
      affiliate: item.affiliate,
      rating: item.rating,
      cardImage: item.cardImage,
      bankLogo: item.bankLogo
    }));
    setFocused(false);
  };

  /** ---------- TABLE EDIT ACTIONS ---------- */
  const ensureRowWidth = (colsCount) => (row) => {
    const copy = [...row];
    while (copy.length < colsCount) copy.push('');
    while (copy.length > colsCount) copy.pop();
    return copy;
  };
  useEffect(() => {
    if (activeIdx < 0) return;
    setTables(prev => {
      const arr = [...prev];
      const t = { ...arr[activeIdx] };
      t.rows = t.rows.map(ensureRowWidth(t.cols.length));
      arr[activeIdx] = t;
      return arr;
    });
  }, [activeTableId, tables.length]);

  const updateActiveTable = (patch) => {
    if (activeIdx < 0) return;
    setTables(prev => {
      const arr = [...prev];
      arr[activeIdx] = { ...arr[activeIdx], ...(typeof patch === 'function' ? patch(arr[activeIdx]) : patch) };
      return arr;
    });
  };
  const addColumn = () => updateActiveTable(t => ({ ...t, cols: [...t.cols, `Col ${t.cols.length + 1}`], rows: t.rows.map(r => [...r, '']) }));
  const removeColumn = (idx) => updateActiveTable(t => ({ ...t, cols: t.cols.filter((_, i) => i !== idx), rows: t.rows.map(r => r.filter((_, i) => i !== idx)) }));
  const updateHeader = (idx, val) => updateActiveTable(t => ({ ...t, cols: t.cols.map((h, i) => (i === idx ? val : h)) }));
  const addTbRow = () => updateActiveTable(t => ({ ...t, rows: [...t.rows, Array(t.cols.length).fill('')] }));
  const removeTbRow = (i) => updateActiveTable(t => ({ ...t, rows: t.rows.filter((_, idx) => idx !== i) }));
  const updateTbCell = (rIdx, cIdx, val) => updateActiveTable(t => ({ ...t, rows: t.rows.map((r, i) => (i === rIdx ? r.map((c, j) => (j === cIdx ? val : c)) : r)) }));

  // Insert/Update destinations
  const insertIntoContent = () => {
    const q = quillRefs.content?.current?.getEditor?.();
    if (!q) { alert('Open the Main Content editor once, then try again.'); return; }
    const block = buildMetaBlock(activeTable);
    replaceOrInsertInQuill(q, activeTable.binding, block);
  };
  const insertIntoMeta = () => {
    const q = quillRefs.metaDetails?.current?.getEditor?.();
    if (!q) { alert('Open the Meta Details editor once, then try again.'); return; }
    const block = buildMetaBlock(activeTable);
    replaceOrInsertInQuill(q, activeTable.binding, block);
  };
  // NEW: explicit Rates & Fees insert/update actions
  const insertIntoRates = () => {
    const block = buildMetaBlock(activeTable);
    setMeta(prev => ({ ...prev, fees: replaceOrInsertInString(prev.fees || '', activeTable.binding, block) }));
  };
  const updateInRates = () => {
    const block = buildMetaBlock(activeTable);
    setMeta(prev => ({ ...prev, fees: replaceOrInsertInString(prev.fees || '', activeTable.binding, block) }));
  };

  /** ---------- Save ---------- */
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    let previewImageUrl = form.previewImage;
    if (previewImageFile) {
      try { previewImageUrl = await (await uploadAsset(previewImageFile)); }
      catch { alert('Failed to upload preview image.'); setLoading(false); return; }
    }

    // structured tables (preserve for future edit)
    const tablesPayload = tables.map(t => ({
      id: t.id,
      title: t.title,
      hasHeader: t.hasHeader,
      cols: t.cols,
      rows: t.rows,
      binding: t.binding,
      delimiter: t.delimiter,
      rowSeparator: t.rowSeparator,
      delimited: buildDelimited(t)
    }));

    const payload = {
      heading: form.heading,
      content: form.content,
      preview_text: form.previewText,
      tags: form.tags,
      youtube_link: form.youtubeLink,
      status: form.status,
      priority: Number(form.priority) || 1,
      source_id: form.source_id || '1',
      content_type: form.type,
      preview_img: previewImageUrl,
      affiliate: form.affiliate,
      rating: Number(form.rating) || 0,
      card_image: form.cardImage,
      bank_logo: form.bankLogo,
      card_id: form.card_id || 1,
      meta: form.type === 'cc_blog'
        ? {
            ...meta,
            tables: tablesPayload,
            details_csv: tablesPayload.map(t => t.delimited),
            table_keys: tables.map(t => t.binding)
          }
        : undefined
    };

    try {
      const res = await fetch(`https://admin.yaper.co/api/v1/content_pages/${slug}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      await res.json();
      if (Array.isArray(blogs) && setBlogs) {
        setBlogs(blogs.map(b => (String(b.id) === slug ? { ...b, ...payload } : b)));
      }
      navigate('/');
    } catch (err) {
      console.error('Update failed:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (e) => {
    e.preventDefault();
    navigate(`/PreviewBlog?slug=${encodeURIComponent(slug)}`, {
      state: { content_type: form.type || 'blog', form, meta }
    });
  };

  if (loading) return <div style={styles.page}><div style={styles.container}>Loading...</div></div>;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.actionsBar}>
          <button type="button" onClick={handlePreview} disabled={loading} style={styles.buttonDark}>Preview</button>
          <button type="button" onClick={handleSave} disabled={loading} style={styles.buttonPrimary}>{loading ? 'Saving…' : 'Save'}</button>
        </div>

        <div style={styles.divider} />
        <div style={styles.headerRow}><h2 style={styles.heading}>Edit {form.type?.toUpperCase()} Blog</h2></div>

        <form onSubmit={handleSave} style={styles.form}>
          {/* Basic */}
          <div style={styles.section}>
            <div style={styles.row}>
              <div style={styles.control}>
                <label style={styles.label}>Source</label>
                <select name="source_id" value={form.source_id} onChange={handleFormChange} style={styles.select}>
                  <option value="1">Source 1</option><option value="2">Source 2</option><option value="3">Source 3</option>
                </select>
              </div>
              <div style={styles.control}>
                <label style={styles.label}>Status</label>
                <select name="status" value={form.status} onChange={handleFormChange} style={styles.select}>
                  <option value={1}>Active</option><option value={0}>Inactive</option>
                </select>
              </div>
            </div>

            <div style={styles.rowTall}>
              <div style={styles.control}>
                <label style={styles.label}>Preview Image (Upload)</label>
                <input type="file" accept="image/*" onChange={previewImageHandler} style={styles.input} />
              </div>
              <div style={styles.control}>
                <label style={styles.label}>Preview Image URL</label>
                <input type="text" name="previewImage" value={form.previewImage} onChange={handleFormChange} placeholder="Or paste an image URL" style={styles.input} />
              </div>
            </div>

            {form.previewImage && <img src={form.previewImage} alt="Preview" style={styles.previewImage} />}
          </div>

          {/* CC directory */}
          {form.type === 'cc_blog' && (
            <div style={styles.section}>
              <h4 style={{ ...styles.label, marginBottom: '0.5rem' }}>Card Directory</h4>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                {cardsLoading ? 'Loading cards…' : (cardsError ? cardsError : `Loaded ${cards.length} cards`)}
              </div>

              <div style={styles.rowTall}>
                <div style={{ ...styles.control, ...styles.suggestWrap }}>
                  <label style={styles.label}>Card Name</label>
                  <input
                    name="heading"
                    value={form.heading}
                    onChange={handleFormChange}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setTimeout(() => setFocused(false), 150)}
                    placeholder="Start typing to search…"
                    style={styles.input}
                  />
                  {focused && filteredSuggestions.length > 0 && (
                    <div style={styles.suggestList}>
                      {filteredSuggestions.map((s, i) => (
                        <div
                          key={`${s.id}-${s.name}-${i}`}
                          style={{ ...styles.suggestItem, ...(hoverIndex === i ? styles.suggestItemHover : {}) }}
                          onMouseEnter={() => setHoverIndex(i)}
                          onMouseLeave={() => setHoverIndex(-1)}
                          onMouseDown={(e) => { e.preventDefault(); applySuggestion(s); }}
                        >
                          <img src={s.cardImage || 'https://placehold.co/80x48'} alt="" style={styles.thumb} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600 }}>{s.name}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>{s.affiliate || '—'} • {s.rating || 0}★</div>
                          </div>
                          {s.bankLogo ? <img src={s.bankLogo} alt="" style={styles.logo} /> : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={styles.control}>
                  <label style={styles.label}>Affiliate (Link)</label>
                  <input name="affiliate" value={form.affiliate} onChange={handleFormChange} placeholder="https://…" style={styles.input} />
                </div>
              </div>

              <div style={styles.rowTall}>
                <div style={styles.control}>
                  <label style={styles.label}>Rating</label>
                  <input name="rating" type="number" min="0" max="5" step="0.1" value={form.rating} onChange={handleFormChange} placeholder="0–5" style={styles.input} />
                </div>
                <div style={styles.control}>
                  <label style={styles.label}>Card Image URL</label>
                  <input name="cardImage" value={form.cardImage} onChange={handleFormChange} placeholder="https://…" style={styles.input} />
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.control}>
                  <label style={styles.label}>Bank Logo URL</label>
                  <input name="bankLogo" value={form.bankLogo} onChange={handleFormChange} placeholder="https://…" style={styles.input} />
                </div>
              </div>
            </div>
          )}

          {/* Static / Blog content editors */}
          {form.type === 'static' && (
            <div style={styles.section}>
              <label style={styles.label}>Main Content</label>
              <div style={styles.quillContainer}>
                <ReactQuill ref={quillRefs.content} value={form.content} onChange={(val) => setForm(p => ({ ...p, content: val }))} modules={contentModules} formats={formats} />
              </div>
            </div>
          )}
          {form.type === 'blog' && (
            <div style={styles.section}>
              <div style={styles.control}>
                <label style={styles.label}>Heading</label>
                <input name="heading" value={form.heading} onChange={handleFormChange} placeholder="Heading" style={styles.input} />
              </div>
              <label style={styles.label}>Preview Text</label>
              <div style={styles.quillContainer}>
                <ReactQuill ref={quillRefs.previewText} value={form.previewText} onChange={(val) => setForm(p => ({ ...p, previewText: val }))} modules={previewTextModules} formats={formats} />
              </div>
              <label style={{ ...styles.label, marginTop: '1rem' }}>Main Content</label>
              <div style={styles.quillContainer}>
                <ReactQuill ref={quillRefs.content} value={form.content} onChange={(val) => setForm(p => ({ ...p, content: val }))} modules={contentModules} formats={formats} />
              </div>
            </div>
          )}

          {form.type === 'cc_blog' && (
            <div style={styles.section}>
              <div style={styles.row}>
                <div style={styles.control}>
                  <label style={styles.label}>Card Name (Heading)</label>
                  <input name="heading" value={form.heading} onChange={handleFormChange} placeholder="Card Name (Used as heading)" style={styles.input} />
                </div>
                <div style={styles.control}>
                  <label style={styles.label}>YouTube Link</label>
                  <input name="youtubeLink" value={form.youtubeLink} onChange={handleFormChange} placeholder="YouTube Link" style={styles.input} />
                </div>
              </div>

              <div style={styles.rowTall}>
                <div style={styles.control}>
                  <label style={styles.label}>Priority (Trending Order)</label>
                  <input name="priority" type="number" value={form.priority} onChange={handleFormChange} placeholder="Priority" style={styles.input} />
                </div>
                <div style={styles.control}>
                  <label style={styles.label}>Tags (comma-separated)</label>
                  <input name="tags" value={form.tags} onChange={handleFormChange} placeholder="tag1, tag2" style={styles.input} />
                </div>
              </div>

              <h4 style={{ ...styles.label, marginTop: '0.25rem', marginBottom: '0.25rem' }}>Meta Information</h4>

              <div style={styles.rowTall}>
                <textarea name="overview" value={meta.overview || ''} onChange={handleMetaChange} placeholder="Short Description (Overview)" style={styles.textarea} />
                {/* Benefits & Features as Quill */}
                <div style={styles.fullWidth}>
                  <label style={styles.label}>Benefits &amp; Features</label>
                  <div style={styles.quillContainer}>
                    <ReactQuill
                      ref={quillRefs.benefits}
                      value={meta.benefits || ''}
                      onChange={(val) => setMeta(prev => ({ ...prev, benefits: val ?? '' }))}
                      modules={benefitsModules}
                      formats={formats}
                      theme="snow"
                    />
                  </div>
                </div>
              </div>

              {/* Rates & Fees textarea (manual edit). Table Builder buttons below can insert/update blocks into this field */}
              <label style={styles.label}>Rates &amp; Fees</label>
              <textarea name="fees" value={meta.fees || ''} onChange={handleMetaChange} placeholder="Rates & Fees" style={styles.textareaWide} />

              <div style={styles.fullWidth}>
                <label style={styles.label}>Meta Details (Rich Text)</label>
                <div style={styles.quillContainer}>
                  <ReactQuill ref={quillRefs.metaDetails} value={meta.details || ''} onChange={(val) => setMeta(prev => ({ ...prev, details: val }))} modules={metaDetailsModules} formats={formats} />
                </div>
              </div>
            </div>
          )}

          {/* TABLE BUILDER */}
          {(form.type === 'cc_blog' || form.type === 'blog') && (
            <div style={styles.section}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <h3 style={{ margin: 0 }}>Table Builder</h3>
                <div style={{ color: '#6b7280' }}>
                  Create multiple tables, then insert/update in {form.type === 'blog' ? 'Content' : 'Meta Details or Rates & Fees'}.
                </div>
              </div>

              {/* picker */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
                <select value={activeTableId || ''} onChange={(e) => setActiveTableId(e.target.value)} style={styles.select}>
                  {tables.map((t, i) => (
                    <option key={t.id} value={t.id}>{t.binding || `table_${i + 1}`}</option>
                  ))}
                </select>
                <button
                  type="button"
                  style={styles.smallBtn}
                  onClick={() => {
                    const n = tables.length + 1;
                    const t = {
                      id: crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
                      title: '', // no default title text
                      hasHeader: true,
                      cols: ['Label', 'Value'],
                      rows: [['', '']],
                      delimiter: ',',
                      rowSeparator: ';',
                      binding: `table_${n}`
                    };
                    setTables(prev => [...prev, t]);
                    setActiveTableId(t.id);
                  }}
                >
                  + New table
                </button>
                {tables.length > 1 && (
                  <button
                    type="button"
                    style={styles.dangerBtn}
                    onClick={() => {
                      setTables(prev => prev.filter(t => t.id !== activeTableId));
                      setActiveTableId(prev => {
                        const left = tables.filter(t => t.id !== prev);
                        return left[0]?.id || null;
                      });
                    }}
                  >
                    Delete current
                  </button>
                )}
              </div>

              {/* controls */}
              {activeTable && (
                <>
                  <div style={{ display: 'grid', gap: 12, alignItems: 'center', marginBottom: 8, gridTemplateColumns: 'auto 1fr auto auto' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: '#6b7280', fontSize: 12 }}>Binding</span>
                      <input
                        style={{ ...styles.input, width: 160 }}
                        value={activeTable.binding}
                        onChange={(e) => updateActiveTable({ binding: e.target.value.replace(/\s+/g, '_') })}
                        placeholder="table_1"
                      />
                    </div>

                    <input
                      style={{ ...styles.input, width: '100%' }}
                      placeholder="Optional table title (e.g., Table 1)"
                      value={activeTable.title}
                      onChange={(e) => updateActiveTable({ title: e.target.value })}
                    />

                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={activeTable.hasHeader}
                        onChange={(e) => updateActiveTable({ hasHeader: e.target.checked })}
                      />
                      <span>Include header</span>
                    </label>

                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button type="button" style={styles.smallBtn} onClick={addColumn}>+ Add column</button>
                      <button type="button" style={styles.smallBtn} onClick={addTbRow}>+ Add row</button>
                    </div>
                  </div>

                  {/* headers */}
                  <div style={{ ...styles.tbRow, gridTemplateColumns: `repeat(${activeTable.cols.length}, 1fr) auto` }}>
                    {activeTable.cols.map((h, idx) => (
                      <div key={`h-${idx}`} style={{ display: 'flex', gap: 6 }}>
                        <input style={{ ...styles.input, flex: 1 }} placeholder={`Header ${idx + 1}`} value={h} onChange={(e) => updateHeader(idx, e.target.value)} />
                      </div>
                    ))}
                    <div />
                  </div>

                  {/* remove cols */}
                  <div style={{ ...styles.tbRow, gridTemplateColumns: `repeat(${activeTable.cols.length}, 1fr) auto` }}>
                    {activeTable.cols.map((_, idx) => (
                      <button key={`hc-${idx}`} type="button" style={styles.dangerBtn} onClick={() => removeColumn(idx)} disabled={activeTable.cols.length <= 1}>
                        Remove column {idx + 1}
                      </button>
                    ))}
                    <div />
                  </div>

                  {/* rows */}
                  {activeTable.rows.map((r, rIdx) => (
                    <div key={`r-${rIdx}`} style={{ ...styles.tbRow, gridTemplateColumns: `repeat(${activeTable.cols.length}, 1fr) auto` }}>
                      {activeTable.cols.map((_, cIdx) => (
                        <input key={`c-${rIdx}-${cIdx}`} style={styles.input} placeholder={`Row ${rIdx + 1} · Col ${cIdx + 1}`} value={r[cIdx] ?? ''} onChange={(e) => updateTbCell(rIdx, cIdx, e.target.value)} />
                      ))}
                      <button type="button" style={styles.dangerBtn} onClick={() => removeTbRow(rIdx)}>Remove row</button>
                    </div>
                  ))}

                  {/* action buttons */}
                  <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
                    {form.type === 'blog' && (
                      <>
                        <button type="button" style={styles.smallBtn} onClick={insertIntoContent}>Insert into Content</button>
                        <button type="button" style={styles.smallBtn} onClick={insertIntoContent}>Update in Content</button>
                      </>
                    )}
                    {form.type === 'cc_blog' && (
                      <>
                        <button type="button" style={styles.smallBtn} onClick={insertIntoMeta}>Insert into Meta</button>
                        <button type="button" style={styles.smallBtn} onClick={insertIntoMeta}>Update in Meta</button>
                        <button type="button" style={styles.smallBtn} onClick={insertIntoRates}>Insert into Rates &amp; Fees</button>
                        <button type="button" style={styles.smallBtn} onClick={updateInRates}>Update in Rates &amp; Fees</button>
                      </>
                    )}
                  </div>

                  {/* HTML preview */}
                  <div style={{ marginTop: 12 }}>
                    <div style={{ color: '#6b7280', fontSize: 12 }}>HTML preview</div>
                    <div
                      style={{ marginTop: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.75rem', overflowX: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: buildTableHTML(activeTable) }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
